import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { UUID } from 'angular2-uuid';
import { toJson, toLogMessage } from './prro-log-queue.service';
import { StorageService } from './storage.service';

export type DeviceLogLevel = 'debug' | 'info' | 'warn' | 'error';

export const DEVICE_LOG_LEVELS: readonly DeviceLogLevel[] = ['debug', 'info', 'warn', 'error'];

// One line of the device's log, as the admin reads it (getLogs).
export interface DeviceLogEntry {
  // Grows with time, also across app starts: the admin's cursor for "what is new".
  seq: number;
  time: string;
  level: DeviceLogLevel;
  // console, angular (ErrorHandler), window (uncaught), http, router, app, prro or the caller's
  source: string;
  message: string;
  // JSON of the objects logged with the message, cut to DATA_LENGTH.
  data?: string;
  // The app start the entry belongs to.
  boot: string;
}

export interface GetLogsPayload {
  // The entries after this one, oldest first (follow mode).
  sinceSeq?: number;
  // Without sinceSeq: the newest entries before this one (paging back); none = the newest.
  beforeSeq?: number;
  limit?: number;
  levels?: DeviceLogLevel[];
}

export interface GetLogsResult {
  bootId: string;
  bootedAt: string;
  // The newest entry kept, whatever the filter.
  lastSeq: number | null;
  oldestSeq: number | null;
  total: number;
  entries: DeviceLogEntry[];
  // More entries in the asked direction than were returned.
  hasMore: boolean;
  // sinceSeq is older than entries already dropped from the buffer: some lines were missed.
  gap: boolean;
}

const STORAGE_KEY = 'DEVICE_LOG_ENTRIES';
const MAX_ENTRIES = 2000;
// Characters of the messages and the data kept (and saved).
const MAX_CHARS = 1_500_000;
const MESSAGE_LENGTH = 2000;
const DATA_LENGTH = 4000;
// Saved this long after the first unsaved entry, so a crash or a reload loses at most that much.
const SAVE_DELAY_MS = 5_000;
const DEFAULT_LIMIT = 300;
const MAX_LIMIT = 1000;
// The answer goes through Pos.Ws, RabbitMQ and Erp.Ws: keep it well under their 4 MB.
const RESPONSE_CHARS = 600_000;

// The device's own log for the admin: what the app wrote to the console, uncaught errors, failed
// HTTP requests, page changes and PRRO events, kept in a ring buffer that survives a reload or a
// crash (saved to the device storage). Nothing is sent by itself: the admin asks for it (getLogs).
// Adding never throws and never logs, so a broken console or storage can't loop back into it.
@Injectable({ providedIn: 'root' })
export class DeviceLogService {
  readonly bootId = UUID.UUID().slice(0, 8);
  readonly bootedAt = new Date().toISOString();

  private _entries: DeviceLogEntry[] = [];
  private _chars = 0;
  private _lastSeq = 0;
  // The newest seq dropped from the buffer.
  private _droppedSeq = 0;
  private _adding = false;
  private _started = false;
  private _saveTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly _loaded: Promise<void>;

  constructor(
    private _storage: StorageService,
    private _router: Router,
    private _zone: NgZone,
  ) {
    this._loaded = this._load();
  }

  // Uncaught errors, network and visibility changes, page changes. Once, at app start; the
  // context (platform, version) goes with the "App started" line.
  start(context?: Record<string, unknown>): void {
    if (this._started) {
      return;
    }
    this._started = true;
    this.add('info', 'app', ['App started'], { ...context, bootedAt: this.bootedAt, url: location.href });

    this._zone.runOutsideAngular(() => {
      window.addEventListener('error', (event: ErrorEvent) => {
        const where = event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined;
        this.add('error', 'window', [event.error || event.message], where ? { where } : undefined);
      });
      window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        this.add('error', 'window', ['Unhandled rejection', event.reason]);
      });
      window.addEventListener('online', () => this.add('info', 'app', ['Network online']));
      window.addEventListener('offline', () => this.add('warn', 'app', ['Network offline']));
      document.addEventListener('visibilitychange', () => {
        this.add('info', 'app', [document.hidden ? 'App hidden' : 'App visible']);
        if (document.hidden) {
          this._saveNow();
        }
      });
      window.addEventListener('pagehide', () => this._saveNow());
    });

    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.add('info', 'router', [`Page ${event.urlAfterRedirects}`]);
      } else if (event instanceof NavigationError) {
        this.add('error', 'router', [`Navigation to ${event.url} failed`, event.error]);
      }
    });
  }

  // A log line: strings make the message, objects and errors go to data with the context.
  add(level: DeviceLogLevel, source: string, args: unknown[], context?: Record<string, unknown>): void {
    if (this._adding) {
      return;
    }
    this._adding = true;
    try {
      const { message, data } = formatLogArgs(args, context);
      this._lastSeq = Math.max(this._lastSeq + 1, Date.now() * 1000);
      this._push({
        seq: this._lastSeq,
        time: new Date().toISOString(),
        level,
        source: source || 'app',
        message,
        data,
        boot: this.bootId,
      });
      this._scheduleSave();
    } catch {
      // The line is lost; logging goes on.
    } finally {
      this._adding = false;
    }
  }

  // The admin's getLogs command.
  async getLogs(payload?: GetLogsPayload): Promise<GetLogsResult> {
    await this._loaded;
    const limit = Math.min(Math.max(Math.floor(Number(payload?.limit)) || DEFAULT_LIMIT, 1), MAX_LIMIT);
    const levels = (payload?.levels || []).filter((level) => DEVICE_LOG_LEVELS.includes(level));
    const matching = levels.length ? this._entries.filter((entry) => levels.includes(entry.level)) : this._entries;

    const sinceSeq = toSeq(payload?.sinceSeq);
    const beforeSeq = toSeq(payload?.beforeSeq);
    let entries: DeviceLogEntry[];
    let hasMore: boolean;
    if (sinceSeq !== undefined) {
      const after = matching.filter((entry) => entry.seq > sinceSeq);
      entries = fitFromStart(after.slice(0, limit));
      hasMore = after.length > entries.length;
    } else {
      const before = beforeSeq !== undefined ? matching.filter((entry) => entry.seq < beforeSeq) : matching;
      entries = fitFromEnd(before.slice(-limit));
      hasMore = before.length > entries.length;
    }

    return {
      bootId: this.bootId,
      bootedAt: this.bootedAt,
      lastSeq: this._entries.length ? this._entries[this._entries.length - 1].seq : null,
      oldestSeq: this._entries.length ? this._entries[0].seq : null,
      total: this._entries.length,
      entries,
      hasMore,
      gap: sinceSeq !== undefined && sinceSeq < this._droppedSeq,
    };
  }

  private _push(entry: DeviceLogEntry): void {
    this._entries.push(entry);
    this._chars += entrySize(entry);
    this._trim();
  }

  private _trim(): void {
    let drop = 0;
    while (this._entries.length - drop > MAX_ENTRIES || (this._chars > MAX_CHARS && this._entries.length - drop > 1)) {
      this._chars -= entrySize(this._entries[drop]);
      this._droppedSeq = Math.max(this._droppedSeq, this._entries[drop].seq);
      drop++;
    }
    if (drop) {
      this._entries.splice(0, drop);
    }
  }

  // The entries of the earlier app starts before those logged since this one.
  private async _load(): Promise<void> {
    try {
      const stored = (await this._storage.get(STORAGE_KEY)) as DeviceLogEntry[] | null;
      if (!Array.isArray(stored) || !stored.length) {
        return;
      }
      const current = this._entries;
      const kept = new Set(current.map((entry) => entry.seq));
      const earlier = stored.filter((entry) => typeof entry?.seq === 'number' && !kept.has(entry.seq));
      this._entries = [...earlier, ...current].sort((a, b) => a.seq - b.seq);
      this._chars = this._entries.reduce((sum, entry) => sum + entrySize(entry), 0);
      this._lastSeq = Math.max(this._lastSeq, this._entries[this._entries.length - 1].seq);
      this._trim();
    } catch {
      // Nothing kept can be read: the entries logged from now on are kept.
    }
  }

  private _scheduleSave(): void {
    if (this._saveTimer) {
      return;
    }
    this._zone.runOutsideAngular(() => {
      this._saveTimer = setTimeout(() => {
        this._saveTimer = null;
        void this._save();
      }, SAVE_DELAY_MS);
    });
  }

  private _saveNow(): void {
    if (this._saveTimer) {
      clearTimeout(this._saveTimer);
      this._saveTimer = null;
    }
    void this._save();
  }

  private async _save(): Promise<void> {
    await this._loaded;
    try {
      await this._storage.set(STORAGE_KEY, this._entries);
    } catch {
      // Kept in memory; the next save tries again.
    }
  }
}

// The message from the strings (and an error's or object's text when there is no string), the
// rest as JSON.
export function formatLogArgs(args: unknown[], context?: Record<string, unknown>): { message: string; data?: string } {
  const texts: string[] = [];
  const details: unknown[] = [];
  for (const arg of args || []) {
    if (typeof arg === 'string') {
      texts.push(arg);
    } else if (arg instanceof Error) {
      texts.push(arg.message || arg.name);
      details.push(arg);
    } else if (arg === null || arg === undefined || typeof arg !== 'object') {
      texts.push(String(arg));
    } else {
      details.push(arg);
    }
  }
  if (!texts.length && details.length) {
    texts.push(toLogMessage(details[0]));
  }

  const hasContext = !!context && Object.keys(context).length > 0;
  let data: string | undefined;
  if (details.length || hasContext) {
    const value =
      details.length === 0 ? context : hasContext ? { ...context, details: one(details) } : one(details);
    data = cut(toJson(value), DATA_LENGTH);
  }
  return { message: cut(texts.join(' '), MESSAGE_LENGTH), data };
}

function one(values: unknown[]): unknown {
  return values.length === 1 ? values[0] : values;
}

function cut(value: string, length: number): string {
  return value.length > length ? `${value.slice(0, length)}…` : value;
}

function entrySize(entry: DeviceLogEntry): number {
  return 80 + entry.message.length + (entry.data?.length || 0);
}

function toSeq(value: unknown): number | undefined {
  const seq = Number(value);
  return value === null || value === undefined || value === '' || !Number.isFinite(seq) ? undefined : seq;
}

// The first entries that fit in the answer (follow mode: the rest comes with the next request).
function fitFromStart(entries: DeviceLogEntry[]): DeviceLogEntry[] {
  let size = 0;
  for (let i = 0; i < entries.length; i++) {
    size += entrySize(entries[i]);
    if (size > RESPONSE_CHARS && i > 0) {
      return entries.slice(0, i);
    }
  }
  return entries;
}

// The last entries that fit in the answer (the newest; older ones by paging back).
function fitFromEnd(entries: DeviceLogEntry[]): DeviceLogEntry[] {
  let size = 0;
  for (let i = entries.length - 1; i >= 0; i--) {
    size += entrySize(entries[i]);
    if (size > RESPONSE_CHARS && i < entries.length - 1) {
      return entries.slice(i + 1);
    }
  }
  return entries;
}
